import FormTitle from "../commons/components/form/FormTitle";
import * as React from "react";
import { useState } from "react";
import Field, { FieldKey } from "../commons/model/form/Field";
import sfetch from "../commons/utils/super-fetch";
import { getPayload } from "../commons/utils/form-utils";
import { HealthHistoryInfo } from "./HealthHistoryInfo";
import Form from "../commons/components/form/Form";
import SelectableOption from "../commons/model/SelectableOption";
import InputState from "../commons/model/form/InputState";

export default function HealthHistoryInfoForm({
  healthHistoryInfo,
  fields,
  updateUrl,
  title,
  submitLabel,
  afterSubmit,
}: {
  healthHistoryInfo?: HealthHistoryInfo;
  fields: Field[];
  updateUrl: string;
  title?: string;
  submitLabel?: string;
  afterSubmit?: (form: { [key: string]: InputState }) => any;
}) {
  const [errorMessage, setErrorMessage] = useState(null);

  const submitHealthHistoryInformation = async (form: { [key: string]: InputState }) => {
    let payload = getPayload(
      form,
      fields.map((f) => f.name)
    );

    try {
      await sfetch(updateUrl, {
        method: "PUT",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (afterSubmit) afterSubmit(form);
    } catch (error) {
      setErrorMessage(i18n["Global.TechnicalError"]);
      throw error;
    }
  };

  const extraProps = {
    healthIssues: {
      additionalClass: "full-width",
    },
    currentMedications: {
      additionalClass: "full-width",
    },
    drugAllergies: {
      additionalClass: "full-width",
    },
    takenOsteoporosisMedications: {
      options: [
        new SelectableOption(
          "true",
          i18n["Global.Yes"],
          healthHistoryInfo?.takenOsteoporosisMedications === true
        ),
        new SelectableOption(
          "false",
          i18n["Global.No"],
          healthHistoryInfo?.takenOsteoporosisMedications === false
        ),
      ],
      additionalClass: "one-line medium",
    },
    additionalHealthInformation: {
      additionalClass: "full-width",
    },
  };

  const initialValueAndExtraProps = fields.reduce((acc, field) => {
    const initialValue = findHealthHistoryInformation(field.key, healthHistoryInfo);
    acc[field.name] = {
      initialValue,
      dirty: !!initialValue,
      mandatory: field.mandatory,
      ...extraProps[field.name],
    };
    return acc;
  }, {});

  const fieldsInput = Form.getFieldComponents(fields, initialValueAndExtraProps, FieldKey.getTitle);

  return (
    <>
      {title && <FormTitle title={title} />}
      <Form
        submit={submitHealthHistoryInformation}
        submitLabel={submitLabel ?? i18n["Global.Save"]}
        generalErrorMsg={errorMessage}
      >
        {fieldsInput}
      </Form>
    </>
  );

  function findHealthHistoryInformation(
    key: FieldKey,
    healthHistoryInformation: HealthHistoryInfo
  ) {
    switch (key) {
      case FieldKey.HEALTH_ISSUES:
        return healthHistoryInformation?.healthIssues;
      case FieldKey.CURRENT_MEDICATIONS:
        return healthHistoryInformation?.currentMedications;
      case FieldKey.DRUG_ALLERGIES:
        return healthHistoryInformation?.drugAllergies;
      case FieldKey.TAKEN_OSTEOPOROSIS_MEDICATIONS:
        return healthHistoryInformation?.takenOsteoporosisMedications;
      case FieldKey.ADDITIONAL_HEALTH_INFORMATION:
        return healthHistoryInformation?.additionalHealthInformation;
      default:
        return null;
    }
  }
}
